import React, { Fragment, memo } from 'react';

import {
  ChangeLifestylesFiltersFunc,
  FetchLifestylesFilters,
  FetchLifestylesTotalCount,
  LifestylesFiltersNavBasePath,
  LifestylesI18nCustomBaseTitle
} from '../../lifestylesTypes';

import useLifestylesIndexPageBreadcrumbs from './useLifestylesIndexPageBreadcrumbs';

import { LifestylesIndexPageBreadcrumbsItem } from './components/LifestylesIndexPageBreadcrumbsItem';

import { CheckPermissions } from '../../../../helpers/CheckPermissions';

import { getChangeLifestylesFiltersCategoriesData } from '../../utils/getChangeLifestylesFiltersCategoriesData';

import { LifestylePath } from '../../LifestylePath';
import { LifestylesPermissions } from '../../lifestylesConstants';

import { lifestylesKeys } from '../../../../locales/keys';

interface LifestylesIndexPageBreadcrumbsProps {
  filtersNavBasePath?: LifestylesFiltersNavBasePath;
  i18nCustomBaseTitle?: LifestylesI18nCustomBaseTitle;
  lifestylesFilters: FetchLifestylesFilters;
  changeLifestylesFilters: ChangeLifestylesFiltersFunc;
  lifestylesTotalCount: FetchLifestylesTotalCount;
}

function LifestylesIndexPageBreadcrumbs({
  filtersNavBasePath,
  i18nCustomBaseTitle,
  lifestylesFilters,
  changeLifestylesFilters,
  lifestylesTotalCount
}: LifestylesIndexPageBreadcrumbsProps) {
  const {
    currentParentCategory,
    currentChildCategory,
    lifestyleCategories,
    handleResetCategoriesFilters,
    handleParentCategoryChangeFilters
  } = useLifestylesIndexPageBreadcrumbs({
    lifestylesFilters,
    changeLifestylesFilters
  });

  return (
    <div className="flex-1 truncate flex pl-3 pr-1 h-full items-center text-base xl:text-lg text-gray-500 space-x-1.5">
      <LifestylesIndexPageBreadcrumbsItem
        dataGa="lifestyles-category-breadcrumb-lifestyles"
        href={filtersNavBasePath}
        active={!currentChildCategory && !currentParentCategory}
        i18nText={i18nCustomBaseTitle || lifestylesKeys.plural}
        filtersNavBasePath={filtersNavBasePath}
        onChangeFilters={handleResetCategoriesFilters}
      />
      {currentParentCategory ? (
        <>
          {currentParentCategory.parent && (
            <>
              <span>/</span>
              <LifestylesIndexPageBreadcrumbsItem
                dataGa={`lifestyles-category-breadcrumb-${
                  currentParentCategory.parent.nameEn ||
                  currentParentCategory.parent.localizedName
                }`}
                href={
                  filtersNavBasePath
                    ? LifestylePath.indexFiltersCustomBaseQuery(
                        filtersNavBasePath,
                        getChangeLifestylesFiltersCategoriesData({
                          lifestyleCategory: currentParentCategory.parent,
                          categories: lifestyleCategories
                        }).changeFilters
                      )
                    : '#'
                }
                active={!currentChildCategory}
                text={currentParentCategory.parent.localizedName}
                filtersNavBasePath={filtersNavBasePath}
                onChangeFilters={handleParentCategoryChangeFilters}
              />
            </>
          )}

          <span>/</span>
          <LifestylesIndexPageBreadcrumbsItem
            dataGa={`lifestyles-category-breadcrumb-${
              currentParentCategory.nameEn ||
              currentParentCategory.localizedName
            }`}
            href={
              filtersNavBasePath
                ? LifestylePath.indexFiltersCustomBaseQuery(
                    filtersNavBasePath,
                    getChangeLifestylesFiltersCategoriesData({
                      lifestyleCategory: currentParentCategory,
                      categories: lifestyleCategories
                    }).changeFilters
                  )
                : '#'
            }
            active={!currentChildCategory}
            text={currentParentCategory.localizedName}
            filtersNavBasePath={filtersNavBasePath}
            onChangeFilters={handleParentCategoryChangeFilters}
          />
        </>
      ) : null}

      {currentChildCategory ? (
        <>
          <span>/</span>
          <LifestylesIndexPageBreadcrumbsItem
            active={true}
            dataGa={`lifestyles-category-breadcrumb-${
              currentChildCategory.nameEn || currentChildCategory.localizedName
            }`}
            text={currentChildCategory.localizedName}
            filtersNavBasePath={filtersNavBasePath}
          />
        </>
      ) : null}

      {lifestylesTotalCount ? (
        <CheckPermissions
          action={LifestylesPermissions.READ_LIFESTYLES_TOTAL_COUNT}
        >
          <span className="py-1 px-3 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-600 text-black dark:text-white leading-tight ml-2">
            {lifestylesTotalCount}
          </span>
        </CheckPermissions>
      ) : null}
    </div>
  );
}

export default memo<LifestylesIndexPageBreadcrumbsProps>(
  LifestylesIndexPageBreadcrumbs
);
